/**
 * Sidebar Component
 * გადატანადი Sidebar კომპონენტი
 */

import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { clsx } from 'clsx';
import type { MenuItem } from '../../types';

export interface SidebarProps {
  items: MenuItem[];
  logo?: React.ReactNode;
  logoCollapsed?: React.ReactNode;
  footer?: React.ReactNode;
  collapsed?: boolean;
  onCollapsedChange?: (collapsed: boolean) => void;
  className?: string;
}

export function Sidebar({
  items,
  logo,
  logoCollapsed,
  footer,
  collapsed: controlledCollapsed,
  onCollapsedChange,
  className,
}: SidebarProps) {
  const [internalCollapsed, setInternalCollapsed] = useState(false);
  const location = useLocation();

  const collapsed = controlledCollapsed ?? internalCollapsed;
  const setCollapsed = onCollapsedChange ?? setInternalCollapsed;

  const isActive = (path?: string) => {
    if (!path) return false;
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  return (
    <aside
      className={clsx(
        'flex flex-col bg-gray-900 text-white transition-all duration-300',
        collapsed ? 'w-16' : 'w-64',
        className
      )}
    >
      {/* Logo */}
      <div className="flex items-center justify-between h-16 px-4 border-b border-r border-gray-200 bg-white">
        <div className={clsx('transition-opacity', collapsed && 'opacity-0')}>
          {logo}
        </div>
        {collapsed && logoCollapsed}
        <button
          onClick={() => setCollapsed(!collapsed)}
          className="p-1 rounded-lg hover:bg-gray-800 text-gray-400 hover:text-white"
        >
          <svg
            className={clsx('w-5 h-5 transition-transform', collapsed && 'rotate-180')}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
          </svg>
        </button>
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto py-4">
        <ul className="space-y-1 px-2">
          {items.map((item) => {
            // Check if this is a section header (no path, no children, no onClick)
            const isSectionHeader = !item.path && !item.children && !item.onClick;

            if (isSectionHeader) {
              return (
                <li key={item.id} className="pt-4 pb-2">
                  {!collapsed && (
                    <div className="px-3 text-xs font-semibold text-gray-400 uppercase tracking-wider">
                      {item.label}
                    </div>
                  )}
                  {collapsed && (
                    <div className="border-t border-gray-700 mx-2"></div>
                  )}
                </li>
              );
            }

            return (
              <SidebarItem
                key={item.id}
                item={item}
                collapsed={collapsed}
                isActive={isActive(item.path)}
              />
            );
          })}
        </ul>
      </nav>

      {/* Footer */}
      {footer && (
        <div className={clsx('border-t border-gray-800 p-4', collapsed && 'px-2')}>
          {footer}
        </div>
      )}
    </aside>
  );
}

interface SidebarItemProps {
  item: MenuItem;
  collapsed: boolean;
  isActive: boolean;
  depth?: number;
}

function SidebarItem({ item, collapsed, isActive, depth = 0 }: SidebarItemProps) {
  const [expanded, setExpanded] = useState(false);
  const hasChildren = item.children && item.children.length > 0;

  const content = (
    <>
      {item.icon && (
        <span className="flex-shrink-0 w-5 h-5">{item.icon}</span>
      )}
      {!collapsed && (
        <>
          <span className="flex-1 truncate">{item.label}</span>
          {item.badge && (
            <span className="px-2 py-0.5 text-xs font-medium bg-primary-600 rounded-full">
              {item.badge}
            </span>
          )}
          {hasChildren && (
            <svg
              className={clsx('w-4 h-4 transition-transform', expanded && 'rotate-90')}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          )}
        </>
      )}
    </>
  );

  const baseClasses = clsx(
    'flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors',
    depth > 0 && 'ml-4',
    isActive
      ? 'bg-primary-600 text-white'
      : 'text-gray-300 hover:bg-gray-800 hover:text-white',
    collapsed && 'justify-center px-2'
  );

  if (item.path && !hasChildren) {
    return (
      <li>
        <Link to={item.path} className={baseClasses} title={collapsed ? item.label : undefined}>
          {content}
        </Link>
      </li>
    );
  }

  if (item.onClick) {
    return (
      <li>
        <button
          onClick={item.onClick}
          className={clsx(baseClasses, 'w-full text-left')}
          title={collapsed ? item.label : undefined}
        >
          {content}
        </button>
      </li>
    );
  }

  if (hasChildren) {
    return (
      <li>
        <button
          onClick={() => setExpanded(!expanded)}
          className={clsx(baseClasses, 'w-full')}
          title={collapsed ? item.label : undefined}
        >
          {content}
        </button>
        {expanded && !collapsed && (
          <ul className="mt-1 space-y-1">
            {item.children!.map((child) => (
              <SidebarItem
                key={child.id}
                item={child}
                collapsed={collapsed}
                isActive={false}
                depth={depth + 1}
              />
            ))}
          </ul>
        )}
      </li>
    );
  }

  return null;
}
